/**
 * TAT Metrics Card Component
 *
 * Displays Turnaround Time metrics
 */

import { Loader2, Clock, TrendingUp, TrendingDown } from 'lucide-react'
import { Card, CardHeader, CardTitle, CardContent } from '@/presentation/components/ui/Card'
import { useTatMetrics } from '@/application/dashboard'
import { cn } from '@/lib/utils'

/**
 * Format hours to a readable string
 */
function formatHours(hours: number): string {
  if (hours < 1) {
    return `${Math.round(hours * 60)} min`
  }
  if (hours < 24) {
    return `${hours.toFixed(1)} h`
  }
  const days = Math.floor(hours / 24)
  const remainingHours = hours % 24
  return `${days}d ${remainingHours.toFixed(0)}h`
}

interface MetricRowProps {
  label: string
  value: number
  icon?: React.ReactNode
  variant?: 'default' | 'good' | 'warning' | 'bad'
}

function MetricRow({ label, value, icon, variant = 'default' }: MetricRowProps) {
  return (
    <div className="flex items-center justify-between py-2">
      <div className="flex items-center gap-2">
        {icon && (
          <span
            className={cn(
              'text-[var(--foreground-muted)]',
              variant === 'good' && 'text-green-500',
              variant === 'warning' && 'text-amber-500',
              variant === 'bad' && 'text-red-500'
            )}
          >
            {icon}
          </span>
        )}
        <span className="text-sm text-[var(--foreground-muted)]">{label}</span>
      </div>
      <span
        className={cn(
          'font-semibold',
          variant === 'good' && 'text-green-600 dark:text-green-400',
          variant === 'warning' && 'text-amber-600 dark:text-amber-400',
          variant === 'bad' && 'text-red-600 dark:text-red-400'
        )}
      >
        {formatHours(value)}
      </span>
    </div>
  )
}

export function TatMetricsCard() {
  const { data, isLoading, isError } = useTatMetrics()

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Tiempo de Respuesta (TAT)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-[150px]">
            <Loader2 className="h-8 w-8 animate-spin text-[var(--primary)]" />
          </div>
        </CardContent>
      </Card>
    )
  }

  if (isError || !data) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Tiempo de Respuesta (TAT)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-[150px] text-[var(--foreground-muted)]">
            Error al cargar datos
          </div>
        </CardContent>
      </Card>
    )
  }

  const { metrics } = data

  // Determine variants based on thresholds
  const avgVariant =
    metrics.avgTatHours < 4 ? 'good' : metrics.avgTatHours < 8 ? 'warning' : 'bad'
  const p95Variant =
    metrics.percentile95Hours < 12
      ? 'good'
      : metrics.percentile95Hours < 24
      ? 'warning'
      : 'bad'

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Clock className="h-5 w-5" />
          Tiempo de Respuesta (TAT)
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-xs text-[var(--foreground-muted)] mb-4">
          Últimos 30 días
        </p>
        <div className="space-y-1 divide-y divide-[var(--border)]">
          <MetricRow
            label="Promedio"
            value={metrics.avgTatHours}
            icon={<TrendingUp className="h-4 w-4" />}
            variant={avgVariant}
          />
          <MetricRow
            label="Mediana"
            value={metrics.medianTatHours}
          />
          <MetricRow
            label="Mínimo"
            value={metrics.minTatHours}
            icon={<TrendingDown className="h-4 w-4" />}
            variant="good"
          />
          <MetricRow
            label="Máximo"
            value={metrics.maxTatHours}
          />
          <MetricRow
            label="Percentil 95"
            value={metrics.percentile95Hours}
            variant={p95Variant}
          />
        </div>
      </CardContent>
    </Card>
  )
}
